import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import calendar from 'dayjs/plugin/calendar';

dayjs.extend(relativeTime);
dayjs.extend(calendar);

const debounce = <T extends (...args: any[]) => void>(fn: T, delay: number): T => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  return function (this: unknown, ...args: Parameters<T>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  } as T;
};

const formatDateSevenDaysBack = (dateString: string): string => {
  const date = dayjs(dateString);
  const now = dayjs();
  const daysDifference = now.diff(date, 'day');

  if (daysDifference === 0) {
    return `Today at ${date.format('hh:mm a').toLowerCase()}`;
  } if (daysDifference === 1) {
    return `Yesterday at ${date.format('hh:mm a').toLowerCase()}`;
  } if (daysDifference <= 7) {
    return `${date.format('dddd')} at ${date.format('hh:mm a').toLowerCase()}`;
  }
  return date.format('MM/DD/YY');
};

const joinDateAndTime = (dateStart, timeStart) => {
  const date = new Date(dateStart);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  let hours: number = parseInt(timeStart.hh);
  const minutes = timeStart.mm;
  const period = timeStart.a;

  if (period === 'pm' && hours < 12) {
    hours += 12;
  } else if (period === 'am' && hours === 12) {
    hours = 0;
  }

  const hoursStr = String(hours).padStart(2, '0');

  const formattedDateTime = `${year}-${month}-${day}T${hoursStr}:${minutes}:00.000Z`;
  return formattedDateTime;
};

const unJoinDateAndTime = (isoString) => {
  const date = new Date(isoString);

  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = String(date.getUTCFullYear()).slice(-2); // Get last 2 digits of the year

  const theDate = `${month}/${day}/${year}`;

  let hours = date.getUTCHours();
  const minutes = String(date.getUTCMinutes()).padStart(2, '0');

  const period = hours >= 12 ? 'pm' : 'am';
  if (hours > 12) {
    hours -= 12;
  } else if (hours === 0) {
    hours = 12;
  }

  const time = { hh: String(hours).padStart(2, '0'), mm: minutes, a: period };
  return { theDate, time };
};

function validateTime(time) {
  return time && typeof time === 'object'
    && 'hh' in time && 'mm' in time && 'a' in time
    && time.hh.length > 0 && time.mm.length > 0 && time.a.length > 0;
}

async function resizeAndConvertToPNG(
  file: File,
  maxWidth = 800,
  maxHeight = 400,
  quality = 0.9,
): Promise<File> {
  return await new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('Provided file is not an image'));
      return;
    }

    const reader = new FileReader();

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.onload = (e) => {
      const img = new Image();

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        const minResizeWidth = 300;

        if (width <= minResizeWidth) {
          canvas.width = width;
          canvas.height = height;
        } else if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
          canvas.width = width;
          canvas.height = height;
        } else {
          canvas.width = width;
          canvas.height = height;
        }

        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(img, 0, 0, width, height);

        // Use data URL instead of blob to avoid CSP issues
        const dataUrl = canvas.toDataURL('image/png', quality);

        // Convert data URL to File object
        const byteString = atob(dataUrl.split(',')[1]);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);

        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }

        const compressedFile = new File(
          [ab],
          file.name.replace(/\.[^.]+$/, '.png'),
          { type: 'image/png' },
        );

        resolve(compressedFile);
      };

      img.src = e.target.result as string;
    };

    reader.readAsDataURL(file);
  });
}

async function createThumbnail(
  file: File,
  thumbnailSize = 200,
  quality = 0.9,
): Promise<File> {
  return await new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('Provided file is not an image'));
      return;
    }

    const reader = new FileReader();

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.onload = (e) => {
      const img = new Image();

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const size = thumbnailSize;
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d');

        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        const sourceWidth = img.width;
        const sourceHeight = img.height;
        let sourceX = 0;
        let sourceY = 0;
        const sourceDim = Math.min(sourceWidth, sourceHeight);

        if (sourceWidth > sourceHeight) {
          sourceX = (sourceWidth - sourceHeight) / 2;
        } else if (sourceHeight > sourceWidth) {
          sourceY = (sourceHeight - sourceWidth) / 2;
        }

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
          img,
          sourceX,
          sourceY,
          sourceDim,
          sourceDim,
          0,
          0,
          size,
          size,
        );

        const format = 'image/png';
        const thumbFilename = file.name.replace(/\.[^.]+$/, '-thumb.png');

        // Use data URL instead of blob to avoid CSP issues
        const dataUrl = canvas.toDataURL(format, quality);

        // Convert data URL to File object
        const byteString = atob(dataUrl.split(',')[1]);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);

        for (let i = 0; i < byteString.length; i++) {
          ia[i] = byteString.charCodeAt(i);
        }

        const thumbnailFile = new File(
          [ab],
          thumbFilename,
          { type: format },
        );

        resolve(thumbnailFile);
      };

      img.src = e.target.result as string;
    };

    reader.readAsDataURL(file);
  });
}

export {
  debounce, formatDateSevenDaysBack, joinDateAndTime, validateTime, unJoinDateAndTime, resizeAndConvertToPNG, createThumbnail,
};
